import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowLeft,
  Camera,
  Check,
  ClipboardCheck,
  Copy,
  Film,
  Gauge,
  History,
  Link2,
  Mail,
  MapPin,
  Pencil,
  Printer,
  ShieldCheck,
  Trash2,
  UserRound,
  X,
} from 'lucide-react'
import { Dropzone } from '../components/Dropzone'
import { EmailDialog } from '../components/EmailDialog'
import { EvidencePlayer } from '../components/EvidencePlayer'
import { NoPhotos, PhotoGallery } from '../components/PhotoGallery'
import { Logo, Wordmark } from '../components/Logo'
import {
  Callout,
  DataRow,
  Panel,
  SeverityPill,
  Spinner,
  StatusPill,
} from '../components/ui'
import { api, mediaUrl, uploadEvidence } from '../lib/api'
import { usePortal } from '../lib/portal-context'
import {
  clock,
  cx,
  fileSize,
  formatDateTime,
  formatLongDateTime,
  plateDisplay,
  relativeTime,
} from '../lib/format'
import type { CaseStatus, TimelineEntry, ViolationCase } from '../lib/types'

export function CaseReport() {
  const { caseNumber = '' } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { config, can, violationByCode, violationLabel } = usePortal()

  const [record, setRecord] = useState<ViolationCase | null>(null)
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [emailOpen, setEmailOpen] = useState(false)
  const [auditOpen, setAuditOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [note, setNote] = useState('')

  const canEdit = can('cases:write')
  const canEmail = can('email')
  const canManage = can('status') || can('notes')
  const canDelete = can('cases:delete')
  const canAddEvidence = can('evidence')
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const payload = await api.getCase(caseNumber)
      setRecord(payload.case)
      setUrl(payload.url)
      // Record the view after paint data is ready; merge back if a new audit row was written.
      void api
        .recordCaseView(`${payload.case.caseNumber}-${payload.case.shareToken}`)
        .then((result) => {
          if (result.case) setRecord(result.case)
        })
        .catch(() => {})
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load this case.')
    } finally {
      setLoading(false)
    }
  }, [caseNumber])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (searchParams.get('share') === '1' && record && canEmail) {
      setEmailOpen(true)
      searchParams.delete('share')
      setSearchParams(searchParams, { replace: true })
    }
  }, [record, canEmail, searchParams, setSearchParams])

  const videos = useMemo(
    () => (record?.evidence ?? []).filter((item) => item.kind === 'video'),
    [record?.evidence],
  )
  const photos = useMemo(
    () => (record?.evidence ?? []).filter((item) => item.kind === 'photo'),
    [record?.evidence],
  )

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url || window.location.href)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      setError('Clipboard blocked by the browser — copy the address bar instead.')
    }
  }

  const changeStatus = async (status: CaseStatus) => {
    if (!record) return
    const payload = await api.setStatus(record.caseNumber, status)
    setRecord(payload.case)
  }

  const addNote = async () => {
    if (!record || !note.trim()) return
    const payload = await api.addNote(record.caseNumber, note.trim())
    setRecord(payload.case)
    setNote('')
  }

  const handleFiles = async (files: File[]) => {
    if (!record) return
    setUploading(true)
    setProgress(0)
    setUploadError(null)
    try {
      const payload = await uploadEvidence(record.caseNumber, files, setProgress)
      setRecord(payload.case)
    } catch (cause) {
      setUploadError(cause instanceof Error ? cause.message : 'Upload failed.')
    } finally {
      setUploading(false)
      setProgress(0)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center gap-2 text-[13.5px] text-slate-400">
        <Spinner /> Loading case {caseNumber}…
      </div>
    )
  }

  if (error || !record) {
    return (
      <div className="mx-auto flex min-h-screen max-w-lg flex-col items-center justify-center px-6 text-center">
        <Wordmark height={24} />
        <h1 className="mt-8 text-[20px] font-semibold text-white">Case not found</h1>
        <p className="mt-2 text-[13.5px] leading-relaxed text-slate-400">
          {error ?? `No report exists at case number ${caseNumber}.`} Check the link, or ask the
          sender to resend it.
        </p>
        <Link to="/" className="btn-ghost mt-6">
          <ArrowLeft size={15} />
          Back to the portal
        </Link>
      </div>
    )
  }

  const primary = record.violations[0] ? violationByCode(record.violations[0]) : undefined

  return (
    <div className="min-h-screen bg-ink-950">
      {/* Report top bar */}
      <header className="no-print sticky top-0 z-20 w-full border-b border-white/[0.07] bg-ink-950/85 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-[1300px] flex-wrap items-center gap-2 px-3 py-2.5 sm:gap-3 sm:px-6 sm:py-3">
          <Link to="/" className="min-w-0">
            <Wordmark height={20} subtitle="Violation Report" />
          </Link>

          <div className="ml-auto flex flex-wrap items-center justify-end gap-1.5 sm:gap-2">
            <button type="button" onClick={copyLink} className="btn-ghost shrink-0 px-2.5 sm:px-3.5">
              {copied ? <Check size={15} className="text-emerald-400" /> : <Link2 size={15} />}
              <span className="hidden sm:inline">{copied ? 'Link copied' : 'Copy link'}</span>
            </button>
            <button type="button" onClick={() => window.print()} className="btn-ghost shrink-0 px-2.5 sm:px-3.5">
              <Printer size={15} />
              <span className="hidden sm:inline">Print / PDF</span>
            </button>
            {canEdit ? (
              <Link to={`/new?case=${record.caseNumber}`} className="btn-ghost shrink-0 px-2.5 sm:px-3.5">
                <Pencil size={15} />
                <span className="hidden sm:inline">Edit report</span>
              </Link>
            ) : null}
            {canEmail ? (
              <button
                type="button"
                onClick={() => setEmailOpen(true)}
                className="btn-primary shrink-0 px-2.5 sm:px-3.5"
              >
                <Mail size={15} />
                <span className="hidden sm:inline">Email to management</span>
              </button>
            ) : null}
          </div>
        </div>
      </header>

      {/* Print-only letterhead */}
      <div className="print-only mb-4">
        <div className="flex items-start justify-between border-b border-slate-300 pb-3">
          <Logo variant="dark" height={30} />
          <div className="text-right text-[11px] leading-snug">
            <div className="font-bold uppercase tracking-wider">Traffic Violation Report</div>
            <div>{record.caseNumber}</div>
            <div>{record.hoa?.community}</div>
            <div>{record.hoa?.management}</div>
          </div>
        </div>
        <div className="mt-2 text-[10.5px]">
          Video evidence is playable online at: <strong>{url}</strong>
        </div>
      </div>

      <div className="print-sheet mx-auto min-w-0 max-w-[1300px] px-4 py-7 sm:px-6">
        {/* Case headline */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="rounded-md border border-altair-500/30 bg-altair-500/10 px-2.5 py-1 font-mono text-[13px] font-bold tracking-wider text-altair-300">
              {record.caseNumber}
            </span>
            <StatusPill status={record.status} />
            <SeverityPill severity={record.severity} />
            {record.conditions.childrenPresent ? (
              <span className="pill border-red-500/30 bg-red-500/10 text-red-300">
                <AlertTriangle size={11} />
                Children present
              </span>
            ) : null}
          </div>

          <h1 className="mt-3 break-words text-[30px] font-bold leading-tight tracking-[-0.025em] text-white">
            {record.violations.map(violationLabel).join(' · ') || 'Traffic violation'}
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13.5px] text-slate-400">
            <span className="flex items-center gap-1.5">
              <MapPin size={14} className="text-slate-600" />
              {[record.location.street, record.location.crossStreet].filter(Boolean).join(' at ') ||
                'Location not specified'}
            </span>
            <span>{formatLongDateTime(record.occurredAt)}</span>
            {record.estimatedSpeed ? (
              <span className="flex items-center gap-1.5">
                <Gauge size={14} className="text-slate-600" />~{record.estimatedSpeed} mph in a{' '}
                {record.postedSpeed ?? 25} mph zone
              </span>
            ) : null}
          </div>

          {primary?.cvc ? (
            <p className="mt-2.5 font-mono text-[11.5px] text-slate-500">
              Reference: {record.violations
                .map((code) => violationByCode(code)?.cvc)
                .filter(Boolean)
                .join(' · ')}
            </p>
          ) : null}
        </div>

        <div className="grid min-w-0 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,340px)]">
          {/* Main column */}
          <div className="min-w-0 space-y-6">
            {canAddEvidence ? (
              <div className="no-print">
                <Panel
                  title="Add evidence"
                  bodyClassName="space-y-2"
                >
                  <p className="text-[13px] leading-relaxed text-slate-400">
                    Attach more photos or video to this report without starting over. To reorder or
                    caption files, use Edit.
                  </p>
                  <Dropzone onFiles={handleFiles} uploading={uploading} progress={progress} />
                  {uploadError ? (
                    <p className="text-[12.5px] text-red-300">{uploadError}</p>
                  ) : null}
                </Panel>
              </div>
            ) : null}

            {videos.length ? (
              <section>
                <SectionLabel icon={<Film size={14} />}>
                  Video evidence ({videos.length})
                </SectionLabel>
                <div className="space-y-5">
                  {videos.map((video) => (
                    <div key={video.id}>
                      <div className="no-print">
                        <EvidencePlayer
                          caseId={record.id}
                          evidence={video}
                          markers={record.markers}
                          occurredAt={record.occurredAt}
                        />
                      </div>

                      {/* Print substitute: still frame plus the marker list */}
                      <div className="print-only print-block p-3">
                        {video.poster ? (
                          <img
                            src={mediaUrl(record.id, video.poster)}
                            alt=""
                            className="w-full rounded"
                          />
                        ) : null}
                        <div className="mt-2 text-[11px]">
                          <strong>{video.originalName}</strong>
                          {video.durationSec ? ` · ${clock(video.durationSec)}` : ''} · watch at {url}
                        </div>
                        {record.markers
                          .filter((marker) => marker.evidenceId === video.id)
                          .map((marker) => (
                            <div key={marker.id} className="text-[11px]">
                              {clock(marker.timeSec)} — {marker.label}
                            </div>
                          ))}
                      </div>

                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-slate-500">
                        <span className="text-slate-400">{video.originalName}</span>
                        <span>{fileSize(video.size)}</span>
                        {video.durationSec ? <span>{clock(video.durationSec)}</span> : null}
                        {video.width ? (
                          <span>
                            {video.width}×{video.height}
                          </span>
                        ) : null}
                        {video.caption ? (
                          <span className="w-full text-slate-300">{video.caption}</span>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ) : (
              <Callout tone="warn" title="No video attached">
                This case was filed without video evidence.
              </Callout>
            )}

            <section>
              <SectionLabel icon={<Camera size={14} />}>
                Photo evidence {photos.length ? `(${photos.length})` : ''}
              </SectionLabel>
              {photos.length ? (
                <PhotoGallery caseId={record.id} photos={photos} />
              ) : (
                <NoPhotos />
              )}
            </section>

            {record.narrative ? (
              <Panel title="Reported account of the incident">
                <p className="whitespace-pre-wrap text-[14px] leading-[1.7] text-slate-200">
                  {record.narrative}
                </p>
                {record.priorIncidents ? (
                  <div className="mt-4 border-t border-white/[0.06] pt-4">
                    <div className="label">Prior incidents involving this vehicle</div>
                    <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-slate-300">
                      {record.priorIncidents}
                    </p>
                  </div>
                ) : null}
                {record.witnesses ? (
                  <div className="mt-4 border-t border-white/[0.06] pt-4">
                    <div className="label">Witnesses</div>
                    <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-slate-300">
                      {record.witnesses}
                    </p>
                  </div>
                ) : null}
              </Panel>
            ) : null}

            {record.requestedActions.length ? (
              <Panel title="Requested action from the Association" icon={<ClipboardCheck size={14} />}>
                <ul className="space-y-2">
                  {record.requestedActions.map((code) => (
                    <li key={code} className="flex items-start gap-2.5 text-[13.5px] text-slate-200">
                      <Check size={15} className="mt-0.5 shrink-0 text-altair-400" />
                      {config?.catalog.requestedActions.find((action) => action.code === code)?.label ??
                        code}
                    </li>
                  ))}
                </ul>
              </Panel>
            ) : null}

            <Panel title="Evidence integrity" icon={<ShieldCheck size={14} />}>
              <p className="mb-3 text-[12.5px] leading-relaxed text-slate-400">
                Files are stored exactly as captured. Each SHA-256 checksum below was computed at
                upload; recomputing it on the downloaded file proves the footage has not been altered.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-[12.5px]">
                  <thead>
                    <tr className="border-b border-white/[0.07] text-[10.5px] uppercase tracking-[0.12em] text-slate-500">
                      <th className="py-2 pr-3 font-semibold">File</th>
                      <th className="py-2 pr-3 font-semibold">Size</th>
                      <th className="py-2 pr-3 font-semibold">Captured</th>
                      <th className="py-2 font-semibold">SHA-256</th>
                    </tr>
                  </thead>
                  <tbody>
                    {record.evidence.map((item) => (
                      <tr key={item.id} className="border-b border-white/[0.04] last:border-0">
                        <td className="py-2 pr-3 text-slate-200">{item.originalName}</td>
                        <td className="py-2 pr-3 text-slate-400">{fileSize(item.size)}</td>
                        <td className="py-2 pr-3 text-slate-400">
                          {item.capturedAt ? formatDateTime(item.capturedAt) : '—'}
                        </td>
                        <td className="py-2 font-mono text-[11px] text-slate-500">
                          {item.sha256 ? `${item.sha256.slice(0, 16)}…` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>

          {/* Sidebar */}
          <div className="min-w-0 space-y-5">
            <Panel title="Vehicle" icon={<UserRound size={14} />}>
              <div className="mb-3 rounded-lg border border-white/[0.09] bg-gradient-to-b from-white/[0.06] to-transparent px-4 py-3 text-center">
                <div className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-500">
                  {record.vehicle.plateState || 'CA'} plate
                </div>
                <div className="mt-1 font-mono text-[24px] font-bold tracking-[0.12em] text-white">
                  {plateDisplay(record.vehicle.plate)}
                </div>
              </div>
              <DataRow label="Description">
                {[
                  record.vehicle.color,
                  record.vehicle.year,
                  record.vehicle.make,
                  record.vehicle.model,
                  record.vehicle.bodyStyle,
                ]
                  .filter(Boolean)
                  .join(' ') || '—'}
              </DataRow>
              {record.vehicle.distinguishing ? (
                <DataRow label="Identifying marks">{record.vehicle.distinguishing}</DataRow>
              ) : null}
              <DataRow label="Driver">
                {record.resident.knownResident === 'unknown'
                  ? 'Unknown'
                  : record.resident.knownResident}
              </DataRow>
              {record.resident.name ? <DataRow label="Name">{record.resident.name}</DataRow> : null}
              {record.resident.unit ? <DataRow label="Unit">{record.resident.unit}</DataRow> : null}
            </Panel>

            <Panel title="Incident details">
              <DataRow label="Occurred">{formatDateTime(record.occurredAt)}</DataRow>
              <DataRow label="Reported">{formatDateTime(record.createdAt)}</DataRow>
              <DataRow label="Street">{record.location.street || '—'}</DataRow>
              {record.location.crossStreet ? (
                <DataRow label="Cross street">{record.location.crossStreet}</DataRow>
              ) : null}
              {record.location.nearestAddress ? (
                <DataRow label="Nearest">{record.location.nearestAddress}</DataRow>
              ) : null}
              {record.location.notes ? <DataRow label="Notes">{record.location.notes}</DataRow> : null}
              <DataRow label="Community">{record.hoa?.community}</DataRow>
            </Panel>

            {record.conditions.weather ||
            record.conditions.lighting ||
            record.conditions.traffic ||
            record.conditions.pedestriansPresent ||
            record.conditions.childrenPresent ||
            record.conditions.schoolZone ? (
              <Panel title="Conditions">
                {record.conditions.weather ? (
                  <DataRow label="Weather">{record.conditions.weather}</DataRow>
                ) : null}
                {record.conditions.lighting ? (
                  <DataRow label="Lighting">{record.conditions.lighting}</DataRow>
                ) : null}
                {record.conditions.traffic ? (
                  <DataRow label="Traffic">{record.conditions.traffic}</DataRow>
                ) : null}
                {record.conditions.pedestriansPresent ? (
                  <DataRow label="Pedestrians">Present</DataRow>
                ) : null}
                {record.conditions.childrenPresent ? (
                  <DataRow label="Children">Present</DataRow>
                ) : null}
                {record.conditions.schoolZone ? (
                  <DataRow label="Park / school route">Yes</DataRow>
                ) : null}
              </Panel>
            ) : null}

            <Panel title="Filed by">
              {record.reporter.anonymousToResident ? (
                <Callout tone="info" className="mb-3">
                  The reporter asked to remain unnamed in any notice sent to the resident.
                </Callout>
              ) : null}
              <DataRow label="Name">{record.reporter.name || '—'}</DataRow>
              {record.reporter.address ? (
                <DataRow label="Address">{record.reporter.address}</DataRow>
              ) : null}
              {record.reporter.email ? <DataRow label="Email">{record.reporter.email}</DataRow> : null}
              {record.reporter.phone ? <DataRow label="Phone">{record.reporter.phone}</DataRow> : null}
              <DataRow label="Capture device">{record.captureDevice || '—'}</DataRow>
            </Panel>

            <Panel title="Shareable link" className="no-print">
              <code className="block break-all rounded bg-black/40 px-2.5 py-2 font-mono text-[11.5px] text-altair-300">
                {url}
              </code>
              <button type="button" onClick={copyLink} className="btn-ghost mt-2.5 w-full">
                {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                {copied ? 'Copied to clipboard' : 'Copy link for email'}
              </button>
              <p className="mt-2 text-[11.5px] leading-relaxed text-slate-500">
                Opens for anyone with the link — no account needed.
              </p>
            </Panel>

            {canManage ? (
              <Panel title="Case management" className="no-print" bodyClassName="space-y-3">
                {can('status') ? (
                  <div>
                    <div className="label">Status</div>
                    <select
                      value={record.status}
                      onChange={(event) => void changeStatus(event.target.value as CaseStatus)}
                      className="input input-sm"
                    >
                      {(config?.catalog.statuses ?? []).map((status) => (
                        <option key={status.code} value={status.code}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                {can('notes') ? (
                  <div>
                    <div className="label">Add a note to the timeline</div>
                    <textarea
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                      placeholder="Spoke with the office manager on 8/12; notice being drafted."
                      className="input min-h-[70px] text-[13px]"
                    />
                    <button
                      type="button"
                      onClick={addNote}
                      disabled={!note.trim()}
                      className="btn-ghost mt-2 w-full"
                    >
                      Add note
                    </button>
                  </div>
                ) : null}
              </Panel>
            ) : null}

            <Panel title="Activity" className="no-print">
              <AuditTrailSummary
                timeline={record.timeline}
                createdByName={record.createdByName}
                onOpen={() => setAuditOpen(true)}
              />
              {record.emails.length ? (
                <div className="mt-3 border-t border-white/[0.06] pt-3">
                  <div className="label">Sent to management</div>
                  {record.emails.map((entry) => (
                    <div key={entry.id} className="text-[12px] text-slate-400">
                      {formatDateTime(entry.at)} → {entry.to.join(', ') || 'recipient'}{' '}
                      <span className="text-slate-600">({entry.via})</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </Panel>

            <Panel title="Case timeline" className="print-only">
              <ol className="space-y-2 text-[11px]">
                {[...record.timeline]
                  .filter((entry) => entry.kind !== 'view')
                  .reverse()
                  .map((entry) => (
                    <li key={entry.id}>
                      {formatDateTime(entry.at)} — {entry.actor}: {entry.action}
                      {entry.note ? ` (${entry.note})` : ''}
                    </li>
                  ))}
              </ol>
            </Panel>

            {canDelete || (canEdit && record.status === 'draft') ? (
              <DeleteCase caseNumber={record.caseNumber} />
            ) : null}
          </div>
        </div>

        <footer className="mt-10 border-t border-white/[0.07] pt-5 text-[11.5px] leading-relaxed text-slate-600">
          <p>
            Report {record.caseNumber} · generated {formatDateTime(record.createdAt)} · last updated{' '}
            {formatDateTime(record.updatedAt)}
          </p>
          <p className="mt-1.5 max-w-3xl">
            Submitted to {record.hoa?.management} for {record.hoa?.community}. The streets in this
            community are private and are not routinely patrolled by the Irvine Police Department, so
            enforcement of unsafe driving depends on the Association acting on documented reports like
            this one. This report documents observed conduct and is not a determination of legal fault.
          </p>
        </footer>
      </div>

      {emailOpen ? (
        <EmailDialog
          violationCase={record}
          onClose={() => setEmailOpen(false)}
          onUpdated={setRecord}
        />
      ) : null}

      {auditOpen ? (
        <AuditTrailDialog
          timeline={record.timeline}
          createdByName={record.createdByName}
          caseNumber={record.caseNumber}
          onClose={() => setAuditOpen(false)}
        />
      ) : null}
    </div>
  )
}

function SectionLabel({ icon, children }: { icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <h2 className="mb-3 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.14em] text-slate-400">
      {icon ? <span className="text-slate-600">{icon}</span> : null}
      {children}
    </h2>
  )
}

const AUDIT_STYLE: Record<string, { label: string; className: string }> = {
  create: { label: 'Opened', className: 'bg-sky-500/15 text-sky-300' },
  view: { label: 'Viewed', className: 'bg-white/[0.06] text-slate-400' },
  update: { label: 'Updated', className: 'bg-amber-500/15 text-amber-300' },
  submit: { label: 'Submitted', className: 'bg-emerald-500/15 text-emerald-300' },
  status: { label: 'Status', className: 'bg-violet-500/15 text-violet-300' },
  evidence: { label: 'Evidence', className: 'bg-orange-500/15 text-orange-300' },
  note: { label: 'Note', className: 'bg-white/[0.06] text-slate-300' },
  email: { label: 'Emailed', className: 'bg-altair-500/15 text-altair-300' },
  event: { label: 'Event', className: 'bg-white/[0.06] text-slate-400' },
}

function auditSummary(timeline: TimelineEntry[]) {
  const viewCount = timeline.filter((entry) => entry.kind === 'view').length
  const uniqueViewers = new Set(
    timeline
      .filter((entry) => entry.kind === 'view')
      .map((entry) => entry.actorId || entry.actor),
  ).size
  const latest = [...timeline].sort(
    (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
  )[0]
  return { viewCount, uniqueViewers, latest, total: timeline.length }
}

function AuditTrailSummary({
  timeline,
  createdByName,
  onOpen,
}: {
  timeline: TimelineEntry[]
  createdByName?: string
  onOpen: () => void
}) {
  const { viewCount, uniqueViewers, latest, total } = auditSummary(timeline)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[12.5px] text-slate-500">
        <span>
          <span className="font-semibold text-slate-300">{total}</span> event
          {total === 1 ? '' : 's'}
        </span>
        <span className="text-slate-700">·</span>
        <span>
          <span className="font-semibold text-slate-300">{viewCount}</span> view
          {viewCount === 1 ? '' : 's'}
        </span>
        <span className="text-slate-700">·</span>
        <span>
          <span className="font-semibold text-slate-300">{uniqueViewers}</span> viewer
          {uniqueViewers === 1 ? '' : 's'}
        </span>
      </div>

      {latest ? (
        <p className="truncate text-[12.5px] text-slate-400">
          Latest:{' '}
          <span className="text-slate-300">{latest.actor || 'Unknown'}</span>
          {' · '}
          {latest.action}
          <span className="text-slate-600"> · {relativeTime(latest.at)}</span>
        </p>
      ) : (
        <p className="text-[12.5px] text-slate-500">
          No activity recorded yet
          {createdByName ? ` beyond creation by ${createdByName}` : ''}.
        </p>
      )}

      <button type="button" onClick={onOpen} className="btn-ghost w-full">
        <History size={15} />
        View full audit trail
      </button>
    </div>
  )
}

function AuditTrailDialog({
  timeline,
  createdByName,
  caseNumber,
  onClose,
}: {
  timeline: TimelineEntry[]
  createdByName?: string
  caseNumber: string
  onClose: () => void
}) {
  const ordered = useMemo(
    () => [...timeline].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()),
    [timeline],
  )
  const { viewCount, uniqueViewers, total } = auditSummary(timeline)

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="no-print fixed inset-0 z-50 flex animate-fade-in items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm sm:items-center sm:p-8"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="audit-trail-title"
        className="flex max-h-[min(720px,85vh)] w-full max-w-xl animate-slide-up flex-col overflow-hidden rounded-xl border border-white/[0.09] bg-ink-850 shadow-lift"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-white/[0.07] px-5 py-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.04] text-slate-300">
              <History size={16} />
            </span>
            <div>
              <h2 id="audit-trail-title" className="text-[15px] font-semibold text-white">
                Audit trail
              </h2>
              <p className="text-[12px] text-slate-500">
                {caseNumber}
                {createdByName ? ` · opened by ${createdByName}` : ''}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="btn-subtle p-2" aria-label="Close">
            <X size={17} />
          </button>
        </header>

        <div className="flex shrink-0 flex-wrap gap-x-3 gap-y-1 border-b border-white/[0.06] px-5 py-3 text-[12.5px] text-slate-500">
          <span>
            <span className="font-semibold text-slate-300">{total}</span> events
          </span>
          <span className="text-slate-700">·</span>
          <span>
            <span className="font-semibold text-slate-300">{viewCount}</span> views
          </span>
          <span className="text-slate-700">·</span>
          <span>
            <span className="font-semibold text-slate-300">{uniqueViewers}</span> viewers
          </span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {ordered.length ? (
            <ol className="space-y-3">
              {ordered.map((entry) => {
                const style = AUDIT_STYLE[entry.kind || 'event'] ?? AUDIT_STYLE.event
                return (
                  <li key={entry.id} className="flex gap-3">
                    <span
                      className={cx(
                        'mt-0.5 h-fit shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                        style.className,
                      )}
                    >
                      {style.label}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium text-slate-200">
                        <span className="text-white">{entry.actor || 'Unknown'}</span>
                        <span className="text-slate-500"> · {entry.action}</span>
                      </div>
                      {entry.note ? (
                        <div className="mt-0.5 text-[12px] leading-snug text-slate-400">
                          {entry.note}
                        </div>
                      ) : null}
                      <div className="mt-0.5 text-[11px] text-slate-600">
                        {formatDateTime(entry.at)} · {relativeTime(entry.at)}
                      </div>
                    </div>
                  </li>
                )
              })}
            </ol>
          ) : (
            <p className="py-8 text-center text-[13px] text-slate-500">No activity recorded yet.</p>
          )}
        </div>
      </div>
    </div>
  )
}

function DeleteCase({ caseNumber }: { caseNumber: string }) {
  const [confirming, setConfirming] = useState(false)
  const [working, setWorking] = useState(false)

  const remove = async () => {
    setWorking(true)
    try {
      await api.deleteCase(caseNumber)
      window.location.href = '/'
    } finally {
      setWorking(false)
    }
  }

  return (
    <div className="no-print">
      {confirming ? (
        <div className="rounded-lg border border-red-500/25 bg-red-500/[0.07] px-4 py-3">
          <p className="text-[13px] text-red-100">
            Delete {caseNumber} and every uploaded file? This cannot be undone.
          </p>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={remove} disabled={working} className="btn-danger flex-1">
              {working ? <Spinner /> : <Trash2 size={14} />}
              Delete permanently
            </button>
            <button type="button" onClick={() => setConfirming(false)} className="btn-ghost">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className={cx('btn-subtle w-full text-slate-500 hover:text-red-300')}
        >
          <Trash2 size={14} />
          Delete this case
        </button>
      )}
    </div>
  )
}
